import { ReactElement, use } from 'react';
import { Controller, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';

import WanderingBoundaryRowProviderContext from 'features/wandering-boundary-settings/providers/contexts/wandering-boundary-row-provider-context';
import styles from './wandering-boundary-row-creation.module.scss';

const WanderingBoundaryRowCreation = (): ReactElement => {
  const { t } = useTranslation('wanderingBoundarySettings');
  const { wanderingBoundary, form, submitForm, validationRules, cancelCreateWanderingBoundary } = use(WanderingBoundaryRowProviderContext);

  const nameInputField = (): ReactElement => (
    <Controller
      name="name"
      control={form.control}
      rules={validationRules.name}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          label={t('wanderingBoundaryFields.nameLabel')}
          variant="outlined"
          fullWidth
          error={!!error}
          helperText={error?.message}
          slotProps={{
            htmlInput: {
              maxLength: 30,
            },
          }}
          className={styles.nameField}
        />
      )}
    />
  );

  const descriptionInputField = (): ReactElement => (
    <Controller
      name="description"
      control={form.control}
      render={({ field }) => (
        <TextField
          {...field}
          className={styles.textField}
          slotProps={{
            htmlInput: {
              maxLength: 80,
            },
          }}
          id="descriptionInput"
          label={t('wanderingBoundaryFields.descriptionLabel')}
          variant="outlined"
          multiline
          minRows={6}
          maxRows={6}
        />
      )}
    />
  );

  const noteInputField = (): ReactElement => (
    <Controller
      name="note"
      control={form.control}
      render={({ field }) => (
        <TextField
          {...field}
          className={styles.textField}
          slotProps={{
            htmlInput: {
              maxLength: 255,
            },
          }}
          id="noteInput"
          label={t('wanderingBoundaryFields.noteLabel')}
          variant="outlined"
          multiline
          minRows={6}
          maxRows={6}
        />
      )}
    />
  );

  return (
    <div className={styles.wanderingBoundaryRowContainer}>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(submitForm)}>
          <div className={styles.inputFields}>
            {nameInputField()}
            <div className={styles.descriptionAndNoteRow}>
              {descriptionInputField()}
              {noteInputField()}
            </div>
          </div>
          <div className={styles.buttonsContainer}>
            <IconButton className={styles.cancelButton} onClick={() => cancelCreateWanderingBoundary(wanderingBoundary)}>
              <CloseRoundedIcon />
            </IconButton>
            <IconButton className={styles.checkButton} type="submit">
              <CheckRoundedIcon />
            </IconButton>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default WanderingBoundaryRowCreation;
